import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES, BRAND } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { heroImage } = useSiteContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-ink"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={heroImage}
          alt="Premium motorcycle detailed by MOTO LUXE"
          className="h-full w-full object-cover object-center"
        />
      </motion.div>

      {/* Cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(5,5,5,0.85)_100%)]" />

      {/* Gold light streaks */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -left-1/4 h-px w-1/2 bg-gradient-to-r from-transparent via-gold/50 to-transparent animate-streak" />
        <div className="absolute top-2/3 -left-1/4 h-px w-1/3 bg-gradient-to-r from-transparent via-champagne/40 to-transparent animate-streak" style={{ animationDelay: "2.4s" }} />
      </div>

      <motion.div
        style={{ opacity: overlayOpacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="font-mono text-[11px] sm:text-xs tracking-[0.4em] text-gold uppercase">
            {BRAND.name} — {BRAND.tagline}
          </span>
        </motion.div>

        <h1 className="font-serif uppercase text-cream leading-[0.92] text-5xl sm:text-7xl lg:text-8xl tracking-tight max-w-4xl">
          <Line delay={0.35}>Your Motorcycle.</Line>
          <Line delay={0.5}>
            <span className="gold-text italic">Elevated.</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-md text-cream/70 text-base sm:text-lg font-light leading-relaxed"
        >
          Premium motorcycle detailing for riders who care about every detail.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-book-detail-button"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center justify-center gap-2 bg-gold text-ink px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase hover:bg-champagne transition-colors duration-300"
          >
            Book Your Detail
          </button>
          <button
            data-testid="hero-explore-services-button"
            onClick={() => scrollToId("services")}
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-cream px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Explore Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/50"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown size={16} className="animate-float text-gold" />
      </motion.div>
    </section>
  );
}
